import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Image,
  Linking,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  addCuratorBook,
  getTopFeaturedBooks,
  loadBookRecommendations,
  removeBook,
  setBookFeatured,
  sortBooks,
} from "../storage/bookRecommendationsStorage.js";
import { getBookSceneMeta } from "../content/libraryCatalog.js";
import { BookCoverCard, getBookCoverSource } from "./library/BookCoverCard.js";
import { CinematicScene } from "./library/CinematicScene.js";
import { CF } from "../theme/turquoiseTheme.js";
import { touchUI } from "../utils/touchUI.js";
import { useLanguage } from "../i18n/LanguageContext.js";

/**
 * Home teaser — top featured transformations with link to full library.
 * @param {{ onOpenLibrary: () => void, darkMode?: boolean }} props
 */
export function HomeBookTeaser({ onOpenLibrary, darkMode = false }) {
  const { t } = useLanguage();
  const [books, setBooks] = useState([]);

  useEffect(() => {
    void loadBookRecommendations().then(setBooks);
  }, []);

  const top = getTopFeaturedBooks(books, 2);
  if (top.length === 0) return null;

  const s = darkMode ? darkTeaserStyles : teaserStyles;

  return (
    <View style={s.wrap}>
      <View style={s.headerRow}>
        <Text style={s.title}>{t("books_teaser_title")}</Text>
        <Pressable onPress={onOpenLibrary}>
          <Text style={s.seeAll}>{t("books_see_all")}</Text>
        </Pressable>
      </View>
      {top.map((book) => (
        <BookCoverCard
          key={book.id}
          title={book.title}
          author={book.author}
          blurb={book.blurb}
          coverUrl={book.coverUrl}
          bookId={book.id}
          featured={book.featured}
          onPress={onOpenLibrary}
        />
      ))}
    </View>
  );
}

const teaserStyles = StyleSheet.create({
  wrap: { marginBottom: 16 },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingHorizontal: 2 },
  title: { fontSize: 16, fontWeight: "800", color: "#0F172A" },
  seeAll: { fontSize: 13, fontWeight: "700", color: CF.primaryDark },
});

const darkTeaserStyles = StyleSheet.create({
  wrap: { marginBottom: 16 },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingHorizontal: 2 },
  title: { fontSize: 16, fontWeight: "800", color: CF.primaryBright },
  seeAll: { fontSize: 13, fontWeight: "700", color: CF.cyan },
});

/**
 * Full book library + curator add flow.
 * @param {{ onBack?: () => void, embedded?: boolean, cinematic?: boolean, darkMode?: boolean }} props
 */
export function BookRecommendationsScreen({ onBack, embedded = false, cinematic = false, darkMode = false }) {
  const { t } = useLanguage();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftAuthor, setDraftAuthor] = useState("");
  const [draftBlurb, setDraftBlurb] = useState("");
  const [draftLink, setDraftLink] = useState("");
  const [draftFeatured, setDraftFeatured] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setBooks(sortBooks(await loadBookRecommendations()));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const featured = books.filter((b) => b.featured);
  const others = books.filter((b) => !b.featured);
  const selected = books.find((b) => b.id === selectedId);

  const openAdd = () => {
    setDraftTitle("");
    setDraftAuthor("");
    setDraftBlurb("");
    setDraftLink("");
    setDraftFeatured(true);
    setAddOpen(true);
  };

  const submitAdd = async () => {
    const title = draftTitle.trim();
    if (!title) {
      Alert.alert("Title required", "Enter the book title.");
      return;
    }
    const next = await addCuratorBook(books, {
      title,
      author: draftAuthor,
      blurb: draftBlurb,
      linkUrl: draftLink,
      featured: draftFeatured,
    });
    setBooks(next);
    setAddOpen(false);
    Alert.alert("Added", "Your recommendation is live for users on this device.");
  };

  const confirmRemove = (book) => {
    if (book.source === "default") {
      Alert.alert("Built-in pick", "Default books stay in the list. You can turn off “Top pick” instead.");
      return;
    }
    Alert.alert("Remove book", `Remove "${book.title}" from recommendations?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Remove",
        style: "destructive",
        onPress: async () => setBooks(await removeBook(books, book.id)),
      },
    ]);
  };

  const toggleFeatured = async (book) => {
    setBooks(await setBookFeatured(books, book.id, !book.featured));
  };

  const openLink = async (url) => {
    if (!url) return;
    try {
      const ok = await Linking.canOpenURL(url);
      if (ok) await Linking.openURL(url);
      else Alert.alert("Link", "Could not open this URL.");
    } catch {
      Alert.alert("Link", "Could not open this URL.");
    }
  };

  const renderBook = (book) => {
    if (cinematic) {
      return (
        <BookCoverCard
          key={book.id}
          title={book.title}
          author={book.author}
          blurb={book.blurb}
          coverUrl={book.coverUrl}
          bookId={book.id}
          featured={book.featured}
          onPress={() => setSelectedId(book.id)}
        />
      );
    }
    return (
      <Pressable
        key={book.id}
        style={styles.card}
        onPress={() => setSelectedId(book.id)}
        onLongPress={() => confirmRemove(book)}
      >
        <View style={styles.cardRow}>
          {getBookCoverSource(book) ? (
            <Image source={getBookCoverSource(book)} style={styles.coverImage} resizeMode="cover" />
          ) : (
            <View style={styles.cover}>
              <Text style={styles.coverEmoji}>📖</Text>
            </View>
          )}
          <View style={styles.cardBody}>
            <Text style={styles.cardTitle}>{book.title}</Text>
            <Text style={styles.cardAuthor}>{book.author}</Text>
            {book.blurb ? (
              <Text style={styles.cardBlurb} numberOfLines={2}>
                {book.blurb}
              </Text>
            ) : null}
            <View style={styles.badges}>
              {book.featured ? <Text style={styles.badgeTop}>{t("books_top_pick")}</Text> : null}
              {book.source === "curator" ? <Text style={styles.badgeCurator}>{t("books_your_pick")}</Text> : null}
            </View>
          </View>
        </View>
      </Pressable>
    );
  };

  return (
    <View style={[styles.wrap, embedded && !cinematic && styles.wrapEmbedded, cinematic && styles.wrapCinematic, darkMode && embeddedDark.wrap]}>
      {!embedded ? (
        <>
          <Text style={styles.pageTitle}>{t("books_title")}</Text>
          <Text style={styles.pageSub}>
            {t("books_subtitle")}
          </Text>
        </>
      ) : null}

      <Pressable style={[styles.addBtn, cinematic && styles.addBtnCinematic]} onPress={openAdd}>
        <Text style={styles.addBtnText}>{t("books_add")}</Text>
      </Pressable>

      {loading ? <Text style={styles.muted}>{t("generic_loading")}</Text> : null}

      {!loading && featured.length > 0 ? (
        <>
          {!cinematic ? (
            <Text style={[styles.sectionLabel, darkMode && embeddedDark.sectionLabel]}>{t("books_top_section")}</Text>
          ) : null}
          {featured.map(renderBook)}
        </>
      ) : null}

      {!loading && others.length > 0 ? (
        <>
          {!cinematic ? (
            <Text style={[styles.sectionLabel, darkMode && embeddedDark.sectionLabel]}>{t("books_more_section")}</Text>
          ) : null}
          {others.map(renderBook)}
        </>
      ) : null}

      {!embedded && onBack ? (
        <Pressable style={styles.backBtn} onPress={onBack}>
          <Text style={styles.backBtnText}>{t("generic_back")}</Text>
        </Pressable>
      ) : null}

      <Modal visible={!!selected} animationType="slide" transparent onRequestClose={() => setSelectedId(null)}>
        <View style={styles.modalBackdrop}>
          <ScrollView contentContainerStyle={styles.modalScroll}>
            {selected ? (
              <View style={styles.detailSheet}>
                {getBookCoverSource(selected) ? (
                  <Image
                    source={getBookCoverSource(selected)}
                    style={styles.detailCover}
                    resizeMode="contain"
                    referrerPolicy="no-referrer"
                  />
                ) : cinematic ? (
                  <CinematicScene
                    sceneId={getBookSceneMeta(selected.id).sceneId}
                    height={200}
                    overlay={getBookSceneMeta(selected.id).overlay}
                  />
                ) : null}
                <Text style={[styles.detailTitle, cinematic && styles.detailTitleCinematic]}>{selected.title}</Text>
                <Text style={styles.detailAuthor}>{t("books_by")} {selected.author}</Text>
                {selected.blurb ? <Text style={styles.detailBlurb}>{selected.blurb}</Text> : null}

                <View style={styles.featureRow}>
                  <Text style={styles.featureLabel}>{t("books_show_top")}</Text>
                  <Switch
                    value={selected.featured}
                    onValueChange={() => void toggleFeatured(selected)}
                    trackColor={{ false: "#d1d5db", true: CF.primaryDark }}
                  />
                </View>

                {selected.linkUrl ? (
                  <Pressable style={styles.linkBtn} onPress={() => void openLink(selected.linkUrl)}>
                    <Text style={styles.linkBtnText}>{t("books_open_link")}</Text>
                  </Pressable>
                ) : null}

                {selected.source === "curator" ? (
                  <Pressable style={styles.removeBtn} onPress={() => confirmRemove(selected)}>
                    <Text style={styles.removeBtnText}>{t("books_remove")}</Text>
                  </Pressable>
                ) : null}

                <Pressable onPress={() => setSelectedId(null)} style={styles.closeDetail}>
                  <Text style={styles.closeDetailText}>{t("generic_close")}</Text>
                </Pressable>
              </View>
            ) : null}
          </ScrollView>
        </View>
      </Modal>

      <Modal visible={addOpen} animationType="slide" transparent onRequestClose={() => setAddOpen(false)}>
        <View style={styles.modalBackdrop}>
          <ScrollView contentContainerStyle={styles.modalScroll} keyboardShouldPersistTaps="handled">
            <View style={styles.detailSheet}>
              <Text style={styles.detailTitle}>{t("books_add_title")}</Text>
              <Text style={styles.addHint}>{t("books_add_hint")}</Text>

              <Text style={styles.fieldLabel}>{t("books_field_title")}</Text>
              <TextInput
                style={styles.input}
                value={draftTitle}
                onChangeText={setDraftTitle}
                placeholder={t("books_field_title_placeholder")}
                placeholderTextColor="#9CA3AF"
                maxLength={120}
              />

              <Text style={styles.fieldLabel}>{t("books_field_author")}</Text>
              <TextInput
                style={styles.input}
                value={draftAuthor}
                onChangeText={setDraftAuthor}
                placeholder={t("books_field_author_placeholder")}
                placeholderTextColor="#9CA3AF"
                maxLength={80}
              />

              <Text style={styles.fieldLabel}>{t("books_field_why")}</Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                value={draftBlurb}
                onChangeText={setDraftBlurb}
                placeholder={t("books_field_why_placeholder")}
                placeholderTextColor="#9CA3AF"
                multiline
                maxLength={500}
              />

              <Text style={styles.fieldLabel}>{t("books_field_link")}</Text>
              <TextInput
                style={styles.input}
                value={draftLink}
                onChangeText={setDraftLink}
                placeholder="https://…"
                placeholderTextColor="#9CA3AF"
                autoCapitalize="none"
                keyboardType="url"
                maxLength={500}
              />

              <View style={styles.featureRow}>
                <Text style={styles.featureLabel}>{t("books_top_toggle")}</Text>
                <Switch
                  value={draftFeatured}
                  onValueChange={setDraftFeatured}
                  trackColor={{ false: "#d1d5db", true: CF.primaryDark }}
                />
              </View>

              <Pressable style={styles.linkBtn} onPress={() => void submitAdd()}>
                <Text style={styles.linkBtnText}>{t("books_save")}</Text>
              </Pressable>
              <Pressable onPress={() => setAddOpen(false)} style={styles.closeDetail}>
                <Text style={styles.closeDetailText}>{t("generic_cancel")}</Text>
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1 },
  pageTitle: { fontSize: 22, fontWeight: "800", color: "#111", textAlign: "center", marginBottom: 6 },
  pageSub: { fontSize: 13, color: "#6B7280", textAlign: "center", marginBottom: 14, lineHeight: 19 },
  addBtn: {
    backgroundColor: CF.primaryDark,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
    marginBottom: 16,
  },
  addBtnText: { color: "#fff", fontWeight: "800", fontSize: 14 },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "800",
    color: "#9A3412",
    letterSpacing: 0.8,
    marginBottom: 10,
    marginTop: 4,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#FED7AA",
  },
  cardRow: { flexDirection: "row" },
  cover: {
    width: 48,
    height: 64,
    borderRadius: 8,
    backgroundColor: "#FFEDD5",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  coverImage: {
    width: 56,
    height: 80,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: "#E5E7EB",
  },
  coverEmoji: { fontSize: 22 },
  detailCover: {
    width: "100%",
    height: 280,
    borderRadius: 12,
    marginBottom: 16,
    backgroundColor: "#0F172A",
  },
  cardBody: { flex: 1 },
  cardTitle: { fontSize: 16, fontWeight: "800", color: "#111" },
  cardAuthor: { fontSize: 13, color: "#6B7280", marginTop: 2 },
  cardBlurb: { fontSize: 12, color: "#4B5563", marginTop: 6, lineHeight: 17 },
  badges: { flexDirection: "row", gap: 8, marginTop: 8 },
  badgeTop: {
    fontSize: 10,
    fontWeight: "800",
    color: "#C2410C",
    backgroundColor: "#FFEDD5",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  badgeCurator: {
    fontSize: 10,
    fontWeight: "800",
    color: "#4338CA",
    backgroundColor: "#EEF2FF",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  muted: { color: "#6B7280", fontStyle: "italic", marginBottom: 12 },
  backBtn: {
    marginTop: 8,
    backgroundColor: "#111",
    minHeight: touchUI.minHit,
    paddingVertical: touchUI.primaryPadV,
    paddingHorizontal: 16,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  backBtnText: { color: "#fff", fontWeight: "800", fontSize: touchUI.primaryFont },
  modalBackdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.45)", justifyContent: "flex-end" },
  modalScroll: { flexGrow: 1, justifyContent: "flex-end" },
  detailSheet: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: 20,
    paddingBottom: 32,
  },
  detailTitle: { fontSize: 20, fontWeight: "800", color: "#111" },
  detailAuthor: { fontSize: 14, color: "#6B7280", marginTop: 4, marginBottom: 12 },
  detailBlurb: { fontSize: 15, color: "#374151", lineHeight: 22, marginBottom: 16 },
  featureRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
    paddingVertical: 8,
  },
  featureLabel: { fontSize: 14, fontWeight: "600", color: "#374151", flex: 1, paddingRight: 8 },
  linkBtn: {
    backgroundColor: CF.primaryDark,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
    marginBottom: 8,
  },
  linkBtnText: { color: "#fff", fontWeight: "800" },
  removeBtn: { paddingVertical: 12, alignItems: "center" },
  removeBtnText: { color: "#DC2626", fontWeight: "700" },
  closeDetail: { alignItems: "center", marginTop: 8 },
  closeDetailText: { color: "#2563EB", fontWeight: "600" },
  addHint: { fontSize: 13, color: "#6B7280", marginBottom: 16, lineHeight: 18 },
  fieldLabel: { fontSize: 12, fontWeight: "700", color: "#374151", marginBottom: 6, marginTop: 8 },
  input: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: "#FAFAFA",
  },
  inputMultiline: { minHeight: 88, textAlignVertical: "top" },
  wrapEmbedded: { marginBottom: 8 },
  wrapCinematic: { paddingHorizontal: 16 },
  addBtnCinematic: {
    marginHorizontal: 0,
    backgroundColor: "rgba(13, 148, 136, 0.85)",
    borderWidth: 1,
    borderColor: "rgba(94, 234, 212, 0.4)",
  },
  detailTitleCinematic: { marginTop: 14 },
});

const embeddedDark = StyleSheet.create({
  wrap: {
    backgroundColor: "#132238",
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: "rgba(45, 212, 191, 0.22)",
  },
  sectionLabel: { color: "#5EEAD4" },
});
